import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Star, ChatCircle, Plus } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Testimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [rating, setRating] = useState(5);
  const [review, setReview] = useState('');
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchTestimonials = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/testimonials`);
      setTestimonials(data);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/companies?approved_only=true`);
      setCompanies(data);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  }, []);

  useEffect(() => {
    fetchTestimonials();
    fetchCompanies();
  }, [fetchTestimonials, fetchCompanies]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please login to submit a review');
      return;
    }

    try {
      await axios.post(`${API}/testimonials`, {
        company_id: selectedCompany,
        rating,
        review
      }, { withCredentials: true });
      
      toast.success('Review submitted successfully!');
      setShowForm(false);
      setSelectedCompany('');
      setRating(5);
      setReview('');
      fetchTestimonials();
    } catch (error) {
      toast.error('Failed to submit review');
    }
  };
      <Navbar />

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        size={20}
        weight={i < rating ? 'fill' : 'regular'}
        className={i < rating ? 'text-yellow-500' : 'text-gray-300'}
      />
    ));
  };

  return (
    <div className="min-h-screen bg-background_light">
      <div className="bg-primary text-text_light py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <ChatCircle size={48} weight="duotone" />
            <h1 className="text-5xl sm:text-6xl tracking-tight font-semibold">Customer Testimonials</h1>
          </div>
          <p className="text-xl leading-relaxed text-secondary max-w-4xl">
            Read what our community says about pest control companies. Share your experience.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {user && !showForm && (
          <button
            onClick={() => setShowForm(true)}
            data-testid="add-review-btn"
            className="mb-8 flex items-center gap-2 bg-primary text-text_light px-6 py-3 rounded-sm hover:bg-primary/90 transition-colors"
          >
            <Plus size={20} />
            Write a Review
          </button>
        )}

        {showForm && (
          <div className="bg-white border border-borders rounded-sm p-8 mb-8">
            <h2 className="text-2xl tracking-tight font-medium mb-6">Write Your Review</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block mb-2 font-medium text-sm uppercase tracking-[0.2em]">Company</label>
                <select
                  data-testid="review-company"
                  value={selectedCompany}
                  onChange={(e) => setSelectedCompany(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Select a company...</option>
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>{company.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block mb-2 font-medium text-sm uppercase tracking-[0.2em]">Rating</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRating(star)}
                      className="hover:scale-110 transition-transform"
                    >
                      <Star
                        size={32}
                        weight={star <= rating ? 'fill' : 'regular'}
                        className={star <= rating ? 'text-yellow-500' : 'text-gray-300'}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block mb-2 font-medium text-sm uppercase tracking-[0.2em]">Your Review</label>
                <textarea
                  data-testid="review-text"
                  value={review}
                  onChange={(e) => setReview(e.target.value)}
                  required
                  rows="6"
                  className="w-full px-4 py-3 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Share your experience..."
                ></textarea>
              </div>

              <div className="flex gap-4">
                <button
                  type="submit"
                  data-testid="submit-review"
                  className="bg-primary text-text_light px-6 py-3 rounded-sm hover:bg-primary/90 transition-colors"
                >
                  Submit Review
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="bg-gray-200 text-text_dark px-6 py-3 rounded-sm hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-medium mb-6">
            Recent Reviews ({testimonials.length})
          </h2>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto"></div>
            </div>
          ) : testimonials.length === 0 ? (
            <div className="bg-white border border-borders rounded-sm p-8 text-center">
              <p className="text-gray-600">No reviews yet. Be the first to write one!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {testimonials.map(testimonial => (
                <div key={testimonial.id} className="bg-white border border-borders rounded-sm p-6" data-testid={`testimonial-${testimonial.id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-medium">{testimonial.company_name}</h3>
                      <p className="text-sm text-gray-600">by {testimonial.user_name}</p>
                    </div>
                    <div className="flex gap-1">
                      {renderStars(testimonial.rating)}
                    </div>
                  </div>
                  <p className="text-base leading-relaxed text-gray-700">{testimonial.review}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}